import type {
  CRMUser,
  EnhancedDeal,
  ProjectOnboarding,
  ProjectTask,
  TaskGroup,
  TimberProject,
  UserRole,
} from '../types';
import type { AppSettings } from './appSettings';
import { architectIsInvolvedInProject } from './architectProjectInvolvement';
import { mergeTasksForWorkQueueEligibility } from './archEngineerWorkQueueEligibility';
import { taskInvolvesUser } from './projectTaskUsers';
import { projectHasSilentQaViewer } from './qaProjectAccess';
import { projectHasArchitectUid } from './architectProjectAccess';
import { engineeringFirmsWithCrmEngineers } from './eorEngineerOptions';
import { isArchitectRole, isDesignerRole } from './userRoles';

type EngineeringFirmRow = NonNullable<AppSettings['engineeringFirms']>[number];

/** Active CRM engineer on the EOR firm's directory or company-matched roster. */
function userIsActiveEngineerOnFirm(
  userId: string,
  firms: EngineeringFirmRow[] | undefined,
  firmId: string | undefined,
  crmUsers: CRMUser[] | undefined,
): boolean {
  if (!userId || !firmId || !firms?.length) return false;
  const user = crmUsers?.find((u) => u.uid === userId);
  if (!user?.isActive || user.role !== 'engineer') return false;
  const firm = firms.find((f) => f.id === firmId);
  if (!firm) return false;
  const roster = engineeringFirmsWithCrmEngineers([firm], crmUsers ?? [])[0]?.engineers ?? [];
  const userEmail = user.email?.trim().toLowerCase();
  return roster.some((engineer) => {
    if (engineer.id === userId) return true;
    const email = engineer.email?.trim().toLowerCase();
    return !!userEmail && !!email && email === userEmail;
  });
}

function engineerHasProjectTies(
  project: TimberProject,
  userId: string,
  projectTasks: ProjectTask[],
  taskGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
  options?: {
    crmUsers?: CRMUser[];
    engineeringFirms?: EngineeringFirmRow[];
  },
): boolean {
  if (!userId || !project.id) return false;

  if (projectHasSilentQaViewer(project, userId)) return true;

  const merged = mergeTasksForWorkQueueEligibility(project, projectTasks, taskGroups, projectOnboardings);
  if (merged.some((t) => taskInvolvesUser(t, userId))) return true;

  const userEmail = options?.crmUsers
    ?.find((u) => u.uid === userId)
    ?.email?.trim()
    .toLowerCase();

  for (const group of taskGroups) {
    if (group.projectId !== project.id) continue;
    const eor = group.eorRecord;
    if (!eor?.engineerId) continue;
    if (eor.engineerId === userId) return true;
    if (userIsActiveEngineerOnFirm(userId, options?.engineeringFirms, eor.firmId, options?.crmUsers)) {
      return true;
    }
    if (!userEmail || !options?.engineeringFirms?.length || !eor.firmId) continue;
    const firm = options.engineeringFirms.find((f) => f.id === eor.firmId);
    const engineer = firm?.engineers?.find((e) => e.id === eor.engineerId);
    const engineerEmail = engineer?.email?.trim().toLowerCase();
    if (engineerEmail && engineerEmail === userEmail) return true;
  }

  return false;
}

/** True when the engineer is on a task, is the EOR, or works at the EOR's firm. */
export function engineerIsInvolvedInProject(
  project: TimberProject,
  userId: string,
  projectTasks: ProjectTask[],
  taskGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
  options?: {
    crmUsers?: CRMUser[];
    engineeringFirms?: EngineeringFirmRow[];
  },
): boolean {
  if (project.status === 'completed') return false;
  return engineerHasProjectTies(project, userId, projectTasks, taskGroups, projectOnboardings, options);
}

/** Broader involvement for PDF/file comment deep links. */
export function engineerIsInvolvedInProjectForFileComment(
  project: TimberProject,
  userId: string,
  projectTasks: ProjectTask[],
  taskGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
  options?: {
    crmUsers?: CRMUser[];
    engineeringFirms?: EngineeringFirmRow[];
  },
): boolean {
  return engineerHasProjectTies(project, userId, projectTasks, taskGroups, projectOnboardings, options);
}

/** Include projects where the user was @mentioned on a PDF (even without task/designer ties). */
export function mergeArchEngineerHubProjectsWithPdfMentions(
  baseProjects: TimberProject[],
  allProjects: TimberProject[],
  pdfMentionProjectIds: ReadonlySet<string>,
): TimberProject[] {
  if (pdfMentionProjectIds.size === 0) return baseProjects;
  const seen = new Set(baseProjects.map((p) => p.id).filter(Boolean));
  const extras = allProjects.filter(
    (p) => p.id && pdfMentionProjectIds.has(p.id) && !seen.has(p.id),
  );
  return extras.length > 0 ? [...baseProjects, ...extras] : baseProjects;
}

/** Project Hub sidebar/list scope for architect and engineer roles; all projects for everyone else. */
export function filterTimberProjectsForArchEngineerHub(
  timberProjects: TimberProject[],
  userId: string | undefined,
  role: UserRole | undefined,
  enhancedDeals: EnhancedDeal[],
  projectTasks: ProjectTask[],
  taskGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
  crmUsers: CRMUser[],
  engineeringFirms: EngineeringFirmRow[] = [],
): TimberProject[] {
  if (!userId || (!isDesignerRole(role) && !isArchitectRole(role) && role !== 'engineer')) {
    return timberProjects;
  }

  const dealsById = new Map(enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d] as const));

  return timberProjects.filter((project) => {
    if (!project.id) return false;
    const deal = project.dealId ? dealsById.get(project.dealId) : undefined;
    if (isArchitectRole(role)) {
      return projectHasArchitectUid(project, deal, userId);
    }
    if (isDesignerRole(role)) {
      return architectIsInvolvedInProject(
        project,
        deal,
        userId,
        projectTasks,
        taskGroups,
        projectOnboardings,
        crmUsers,
      );
    }
    return engineerIsInvolvedInProject(project, userId, projectTasks, taskGroups, projectOnboardings, {
      crmUsers,
      engineeringFirms,
    });
  });
}
